import { useEffect, useState } from "react";
import { ArrowLeft, BookOpen, Download, FileText, Mail, MessageCircle, Package, Pencil, Plus, Search, Trash2, X } from "lucide-react";
import { api, API, money } from "@/lib/api";
import { Pagination, usePagination } from "@/components/Pagination";
import TransactionForm, { DocumentPaymentModal } from "@/pages/TransactionForm";
import { useEscape } from "@/lib/useEscape";
import { ContactModal, contactTypeLabels } from "@/components/ContactModal";
import { SearchSelect } from "@/components/SearchSelect";

const workflow = {
  "Invoice Penjualan": { endpoint: "/invoices", title: "Invoice Penjualan", singular: "Invoice", transaction: true, payMode: "RECEIVE", payLabel: "Terima Pembayaran", columns: ["number", "customer", "date", "status", "total"] },
  "Tagihan Pembelian": { endpoint: "/bills", title: "Tagihan Pembelian", singular: "Tagihan", transaction: true, payMode: "PAY", payLabel: "Kirim Pembayaran", columns: ["number", "supplier", "date", "status", "total"] },
  "Daftar Produk": { endpoint: "/products", title: "Daftar Produk", singular: "Produk", master: true, fields: ["name", "sku", "selling_price", "stock"], columns: ["name", "sku", "type", "selling_price", "stock"] },
  "Jurnal Umum": { endpoint: "/journals", title: "Jurnal Umum", singular: "Jurnal", journal: true, columns: ["number", "date", "description", "status", "total_debit"] },
};
const moneyColumns = ["total", "selling_price", "total_debit", "total_credit", "paid", "purchase_price", "subtotal", "tax_amount"];
const STATUS_LABELS = { DRAFT: "Draft", OPEN: "Belum Dibayar", PARTIAL: "Dibayar Sebagian", PAID: "Lunas", POSTED: "Posted", ACTIVE: "Aktif", VOID: "Void" };
const statusLabel = (s) => STATUS_LABELS[s] || s;
const COLUMN_LABELS = { number: "NOMOR", customer: "PELANGGAN", supplier: "SUPPLIER", date: "TANGGAL", status: "STATUS", total: "TOTAL", name: "NAMA", email: "EMAIL", phone: "TELEPON", payment_terms: "TERMIN", sku: "SKU", type: "TIPE", contact_types: "TIPE KONTAK", company: "PERUSAHAAN", selling_price: "HARGA JUAL", stock: "STOK", description: "DESKRIPSI", total_debit: "TOTAL DEBIT" };
const columnLabel = (c) => COLUMN_LABELS[c] || c.replaceAll("_", " ").toUpperCase();
const FIELD_LABELS = { number: "Nomor", customer: "Pelanggan", supplier: "Supplier", date: "Tanggal Transaksi", due_date: "Jatuh Tempo", email: "Email", reference: "Referensi", memo: "Memo", description: "Deskripsi", total: "Total", name: "Nama", sku: "SKU", type: "Tipe", category: "Kategori", unit: "Satuan", code: "Kode", stock: "Stok", min_stock: "Stok Minimum", selling_price: "Harga Jual", purchase_price: "Harga Beli", phone: "Telepon", payment_terms: "Termin Pembayaran", company: "Perusahaan", tag: "Tag" };
const HIDDEN_FIELDS = ["id", "tenant_id", "company_id", "created_at", "created_by", "lines", "permissions", "journal_id", "tax_percent", "subtotal", "tax_amount", "updated_at", "paid", "status", "cogs_amount", "stock_applied", "has_attachment", "attachment_name", "types"];
const fmtDrawerDate = (d) => (d ? d.split("-").reverse().join("/") : "—");

function cellValue(row, column) {
  if (moneyColumns.includes(column)) return money(row[column]);
  if (column === "status") return <span className={`status ${String(row[column]).toLowerCase()}`}>{statusLabel(row[column])}</span>;
  if (column === "contact_types") return contactTypeLabels(row);
  return row[column] ?? "—";
}

function Modal({ children, onClose, wide }) {
  return (
    <div className="modal-overlay" data-testid="form-modal" onClick={wide ? undefined : onClose}>
      <div className={`modal-panel ${wide ? "wide" : ""}`} onClick={(e) => e.stopPropagation()}>{children}</div>
    </div>
  );
}

export default function WorkflowView({ view, autoCreate = false, onCreateConsumed }) {
  const cfg = workflow[view];
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(null);
  const [paying, setPaying] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [formError, setFormError] = useState("");
  const [message, setMessage] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkPaying, setBulkPaying] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const filtered = rows.filter((r) => {
    if (!cfg) return false;
    const matchText = cfg.columns.map((c) => r[c]).join(" ").toLowerCase().includes(search.toLowerCase());
    const matchStatus = !statusFilter || r.status === statusFilter;
    return matchText && matchStatus;
  });
  const pager = usePagination(filtered, 25, `${view}|${search}`);
  useEffect(() => {
    setRows([]); setSearch(""); setSelected(null); setEditing(null); setPaying(null); setDeleting(null); setMessage(null); setSelectedIds([]); setBulkPaying(false); setStatusFilter("");
    const c = workflow[view];
    if (c) api(c.endpoint + (c.type ? `?type=${c.type}` : "")).then(setRows).catch((e) => setMessage({ text: e.message, type: "error" }));
  }, [view]);
  useEffect(() => {
    if (autoCreate && workflow[view]) { setEditing({}); onCreateConsumed && onCreateConsumed(); }
  }, [autoCreate, view]); // eslint-disable-line
  if (!cfg) return (
    <div className="module-placeholder">
      <div className="module-icon"><FileText size={28} /></div>
      <p className="eyebrow">MODUL AIC ACCOUNTING</p>
      <h1>{view}</h1>
      <p className="muted">Modul ini sudah berada di navigasi dan akan dibangun pada fase berikutnya.</p>
    </div>
  );
  const canBulk = Boolean(cfg.payMode);
  const isSelectable = (r) => ["OPEN", "PARTIAL"].includes(r.status) && (r.total || 0) - (r.paid || 0) > 0;
  const selectableFiltered = canBulk ? filtered.filter(isSelectable) : [];
  const allSelected = selectableFiltered.length > 0 && selectableFiltered.every((r) => selectedIds.includes(r.id));
  const toggleAll = () => setSelectedIds(allSelected ? [] : selectableFiltered.map((r) => r.id));
  const toggleOne = (id) => setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const selectedDocs = rows.filter((r) => selectedIds.includes(r.id));
  const selectedTotal = selectedDocs.reduce((s, d) => s + ((d.total || 0) - (d.paid || 0)), 0);
  const bulkLabel = cfg.payMode === "PAY" ? "Bayar Sekaligus" : "Terima Sekaligus";
  const save = async (data, approve = false) => {
    try {
      const isEdit = Boolean(editing?.id);
      const payload = { ...data };
      if (!cfg.transaction) {
        if (cfg.type) payload.type = cfg.type;
        if (cfg.endpoint === "/contacts") payload.payment_terms = payload.payment_terms || "30 hari";
        if (cfg.endpoint === "/products") { payload.selling_price = Number(payload.selling_price) || 0; payload.stock = Number(payload.stock) || 0; }
      }
      let r = await api(isEdit ? `${cfg.endpoint}/${editing.id}` : cfg.endpoint, { method: isEdit ? "PATCH" : "POST", body: JSON.stringify(payload) });
      if (!isEdit && approve && cfg.transaction) r = await api(`${cfg.endpoint}/${r.id}/approve`, { method: "POST" });
      setRows((prev) => (isEdit ? prev.map((x) => (x.id === r.id ? r : x)) : [r, ...prev]));
      setEditing(null);
      setFormError("");
      setMessage({ text: isEdit ? `${cfg.singular} ${r.number || ""} berhasil diperbarui${cfg.transaction ? " — jurnal disesuaikan otomatis" : ""}` : `${cfg.singular} ${r.number || ""} berhasil disimpan${approve && cfg.transaction ? " dan disetujui — menunggu konfirmasi pembayaran" : ""}`, type: "success" });
    } catch (e) {
      if (editing !== null) setFormError(e.message);
      else setMessage({ text: e.message, type: "error" });
    }
  };
  const action = async (actionName) => {
    try {
      const r = await api(`${cfg.endpoint}/${selected.id}/${actionName}`, { method: "POST" });
      setRows((prev) => prev.map((x) => (x.id === r.id ? r : x)));
      setSelected(r);
      setMessage({ text: `${cfg.singular} berhasil ${actionName === "approve" ? "disetujui" : "diposting"}`, type: "success" });
    } catch (e) { setMessage({ text: e.message, type: "error" }); }
  };
  const onPaid = async (payment) => {
    setPaying(null);
    try {
      const fresh = await api(cfg.endpoint + (cfg.type ? `?type=${cfg.type}` : ""));
      setRows(fresh);
      setSelected((prev) => (prev ? fresh.find((x) => x.id === prev.id) || null : null));
    } catch { /* refresh best-effort */ }
    setMessage({ text: `Pembayaran ${payment.number} berhasil dikonfirmasi — jurnal kas ${payment.journal_number} otomatis diposting`, type: "success" });
  };
  const onBulkPaid = async (res) => {
    setBulkPaying(false);
    setSelectedIds([]);
    try { const fresh = await api(cfg.endpoint + (cfg.type ? `?type=${cfg.type}` : "")); setRows(fresh); } catch { /* best-effort refresh */ }
    const noun = cfg.payMode === "RECEIVE" ? "penerimaan" : "pembayaran";
    const docNoun = cfg.payMode === "RECEIVE" ? "invoice" : "tagihan";
    setMessage({ text: `${res.groups} ${noun} dibuat untuk ${res.count} ${docNoun} — total ${money(res.total)}`, type: "success" });
  };
  const remove = async () => {
    try {
      await api(`${cfg.endpoint}/${deleting.id}`, { method: "DELETE" });
      setRows((prev) => prev.filter((x) => x.id !== deleting.id));
      setSelected((prev) => (prev?.id === deleting.id ? null : prev));
      setMessage({ text: cfg.transaction ? `${cfg.singular} "${deleting.number || deleting.name}" dihapus — jurnal & pembayaran terkait dibatalkan` : `${cfg.singular} "${deleting.name}" berhasil dihapus`, type: "success" });
    } catch (e) { setMessage({ text: e.message, type: "error" }); }
    setDeleting(null);
  };
  return (
    <div className="workflow-page">
      <div className="page-heading">
        <div>
          <p className="eyebrow">RUANG KERJA TRANSAKSI</p>
          <h1>{cfg.title}</h1>
          <p className="muted">Kelola data, status, dan aktivitas {cfg.title.toLowerCase()} dari satu tempat.</p>
        </div>
        <button data-testid="workflow-create-button" className="primary-btn" onClick={() => { setEditing({}); setSelected(null); }}><Plus size={16} /> Buat {cfg.singular}</button>
      </div>
      {message && <div data-testid={message.type === "error" ? "workflow-error-message" : "workflow-success-message"} className={`success-message ${message.type === "error" ? "error" : ""}`}>{message.text}<button data-testid="workflow-dismiss-message" onClick={() => setMessage(null)}><X size={14} /></button></div>}
      <div className="workflow-toolbar surface">
        <div className="workflow-search"><Search size={16} /><input data-testid="workflow-search-input" placeholder={`Cari ${cfg.title.toLowerCase()}...`} value={search} onChange={(e) => setSearch(e.target.value)} /></div>
        {cfg.payMode && (
          <select className="wf-status-filter" data-testid="workflow-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">Semua status</option>
            <option value="OPEN">Belum Dibayar</option>
            <option value="PARTIAL">Dibayar Sebagian</option>
            <option value="PAID">Lunas</option>
            <option value="DRAFT">Draft</option>
            <option value="VOID">Void</option>
          </select>
        )}
        <span className="record-count">{filtered.length} data</span>
      </div>
      {canBulk && selectedIds.length > 0 && (
        <div className="bulk-bar surface" data-testid="bulk-pay-bar">
          <strong>{selectedIds.length} dokumen dipilih</strong>
          <span className="muted">Total sisa {money(selectedTotal)}</span>
          <div className="bulk-bar-actions">
            <button className="secondary-btn small" data-testid="bulk-clear-button" onClick={() => setSelectedIds([])}>Bersihkan</button>
            <button className="primary-btn small" data-testid="bulk-pay-button" onClick={() => setBulkPaying(true)}>{bulkLabel} ({selectedIds.length} Invoice)</button>
          </div>
        </div>
      )}
      <div className="surface workflow-table-wrap">
        <table className="workflow-table" data-testid="workflow-table">
          <thead><tr>{canBulk && <th className="check-col"><input type="checkbox" data-testid="bulk-select-all" checked={allSelected} onChange={toggleAll} title="Pilih semua" /></th>}{cfg.columns.map((c) => <th key={c}>{columnLabel(c)}</th>)}<th>AKSI</th></tr></thead>
          <tbody>
            {pager.pageItems.map((row) => (
              <tr key={row.id} onClick={() => setSelected(row)} data-testid={`workflow-row-${row.id}`}>
                {canBulk && <td className="check-col" onClick={(e) => e.stopPropagation()}><input type="checkbox" data-testid={`bulk-select-${row.id}`} disabled={!isSelectable(row)} checked={selectedIds.includes(row.id)} onChange={() => toggleOne(row.id)} /></td>}
                {cfg.columns.map((c, i) => <td key={c} className={i === 0 ? "primary-cell" : ""}>{cellValue(row, c)}</td>)}
                <td>
                  <span className="row-actions">
                    <button data-testid={`workflow-open-${row.id}`} className="table-action" onClick={(e) => { e.stopPropagation(); setSelected(row); }}>Buka</button>
                    {(cfg.master || cfg.transaction) && <button data-testid={`workflow-row-edit-${row.id}`} className="table-action" onClick={(e) => { e.stopPropagation(); setEditing(row); setSelected(null); }}>Edit</button>}
                    {(cfg.master || cfg.transaction) && <button data-testid={`workflow-row-delete-${row.id}`} className="table-action danger" onClick={(e) => { e.stopPropagation(); setDeleting(row); }}>Hapus</button>}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <div className="empty-state" data-testid="workflow-empty-state"><Package size={25} /><strong>Belum ada data</strong><span>Buat data pertama Anda untuk mulai bekerja.</span></div>}
        <Pagination pager={pager} testid="workflow-pagination" />
      </div>
      {editing && cfg.endpoint === "/contacts" && (
        <ContactModal initial={editing.id ? editing : null} defaultTypes={cfg.type ? [cfg.type] : undefined}
          onClose={() => setEditing(null)}
          onSaved={(r) => { setRows((prev) => (editing.id ? prev.map((x) => (x.id === r.id ? r : x)) : [r, ...prev])); setEditing(null); setMessage({ text: `Kontak "${r.name}" berhasil disimpan`, type: "success" }); }} />
      )}
      {editing && cfg.endpoint !== "/contacts" && (
        <Modal wide={cfg.transaction || cfg.journal} onClose={() => { setEditing(null); setFormError(""); }}>
          {formError && <div className="journal-balance bad" data-testid="workflow-form-error">{formError}</div>}
          {cfg.journal
            ? <JournalForm onCancel={() => { setEditing(null); setFormError(""); }} onSave={save} />
            : cfg.transaction
              ? <TransactionForm cfg={cfg} initial={editing.id ? editing : null} onCancel={() => { setEditing(null); setFormError(""); }} onSave={save} />
              : <RecordForm cfg={cfg} initial={editing.id ? editing : null} onCancel={() => { setEditing(null); setFormError(""); }} onSave={save} />}
        </Modal>
      )}
      {paying && <DocumentPaymentModal mode={cfg.payMode} doc={paying} onClose={() => setPaying(null)} onDone={onPaid} onError={(text) => { setPaying(null); setMessage({ text, type: "error" }); }} />}
      {bulkPaying && <BulkPaymentModal cfg={cfg} docs={selectedDocs} onClose={() => setBulkPaying(false)} onDone={onBulkPaid} onError={(text) => { setBulkPaying(false); setMessage({ text, type: "error" }); }} />}
      {deleting && (
        <Modal onClose={() => setDeleting(null)}>
          <div className="form-panel" data-testid="delete-confirm-modal">
            <div className="section-head">
              <div><p className="eyebrow">KONFIRMASI HAPUS</p><h2>Hapus {cfg.singular}?</h2></div>
              <button data-testid="delete-confirm-close" className="icon-btn" onClick={() => setDeleting(null)}><X size={18} /></button>
            </div>
            <p className="muted delete-warning">{cfg.transaction ? `"${deleting.number || deleting.name}" akan dihapus. Jurnal terkait akan dibatalkan (void) dan seluruh pembayaran yang menempel pada dokumen ini ikut terhapus otomatis.` : `"${deleting.name}" akan dihapus permanen. Data yang sudah digunakan pada transaksi tidak dapat dihapus.`}</p>
            <div className="form-actions">
              <button data-testid="delete-confirm-button" className="danger-btn" onClick={remove}>Ya, Hapus</button>
              <button data-testid="delete-cancel-button" className="secondary-btn" onClick={() => setDeleting(null)}>Batal</button>
            </div>
          </div>
        </Modal>
      )}
      {selected && <DetailDrawer cfg={cfg} selected={selected} onClose={() => setSelected(null)} onAction={action} onPay={() => setPaying(selected)} onEdit={() => { setEditing(selected); setSelected(null); }} onDelete={() => setDeleting(selected)} />}
    </div>
  );
}

function DetailDrawer({ cfg, selected, onClose, onAction, onPay, onEdit, onDelete }) {
  useEscape(onClose);
  const outstanding = (selected.total || 0) - (selected.paid || 0);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfError, setPdfError] = useState("");
  const isDoc = ["/invoices", "/bills"].includes(cfg.endpoint);
  const party = selected.customer || selected.supplier || "";
  const [recipient, setRecipient] = useState(selected.email || "");
  useEffect(() => {
    if (!isDoc || selected.email || !party) return;
    let active = true;
    api("/contacts").then((cs) => { const c = cs.find((x) => x.name === party); if (active && c?.email) setRecipient(c.email); }).catch(() => {});
    return () => { active = false; };
  }, [isDoc, party, selected.email]);
  const downloadPdf = async () => {
    setPdfBusy(true); setPdfError("");
    try {
      const r = await fetch(`${API}${cfg.endpoint}/${selected.id}/pdf`, { credentials: "include" });
      if (!r.ok) throw new Error("Gagal membuat PDF");
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${(selected.number || "dokumen").replaceAll("#", "").replaceAll(" ", "-")}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { setPdfError(e.message); }
    setPdfBusy(false);
  };
  const shareText = `Halo ${party},%0A%0ABerikut ${cfg.singular.toLowerCase()} *${selected.number}* sebesar *${money(selected.total || 0)}*${selected.due_date ? ` dengan jatuh tempo ${selected.due_date.split("-").reverse().join("-")}` : ""}.%0ADokumen PDF terlampir.%0A%0ATerima kasih.`;
  const shareWhatsApp = () => window.open(`https://api.whatsapp.com/send?text=${shareText}`, "_blank");
  const shareEmail = () => { window.location.href = `mailto:${recipient}?subject=${encodeURIComponent(`${cfg.singular} ${selected.number}`)}&body=${shareText.replaceAll("%0A", "%0D%0A").replaceAll("*", "")}`; };
  const fieldValue = (k, v) => {
    if (v == null || v === "") return "—";
    if (typeof v === "number" && moneyColumns.includes(k)) return money(v);
    if (["date", "due_date"].includes(k)) return fmtDrawerDate(v);
    return String(v);
  };
  const visibleFields = Object.entries(selected).filter(([k]) => !HIDDEN_FIELDS.includes(k) && !(isDoc && k === "total"));
  return (
    <>
    <div className="drawer-backdrop" data-testid="workflow-drawer-backdrop" onClick={onClose} />
    <div className="detail-drawer v2" data-testid="workflow-detail-drawer">
      <div className="drawer-hero">
        <div className="drawer-hero-top">
          <div>
            <p className="eyebrow">DETAIL {cfg.singular.toUpperCase()}</p>
            <h2>{selected.number || selected.name || selected.description}</h2>
            {party && <p className="drawer-hero-party">{party}</p>}
          </div>
          <button data-testid="workflow-close-detail" className="icon-btn light" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="drawer-hero-meta">
          <span className={`status ${String(selected.status || "ACTIVE").toLowerCase()}`}>{statusLabel(selected.status || "ACTIVE")}</span>
          <span>Dibuat {selected.created_at ? new Date(selected.created_at).toLocaleDateString("id-ID") : "hari ini"}</span>
        </div>
        {isDoc && (
          <div className="drawer-hero-stats" data-testid="drawer-hero-stats">
            <div><small>Total</small><b>{money(selected.total || 0)}</b></div>
            <div><small>Sudah Dibayar</small><b>{money(selected.paid || 0)}</b></div>
            <div className={outstanding > 0 ? "warn" : "ok"}><small>Sisa Tagihan</small><b>{money(outstanding)}</b></div>
          </div>
        )}
      </div>
      <div className="drawer-body">
        <div className="drawer-section-title">Informasi {cfg.singular}</div>
        <div className="detail-fields v2">
          {visibleFields.map(([k, v]) => (
            <div key={k}><small>{FIELD_LABELS[k] || k.replaceAll("_", " ")}</small><strong>{fieldValue(k, v)}</strong></div>
          ))}
        </div>
        {cfg.transaction && (selected.lines || []).length > 0 && (
          <div className="doc-lines v2" data-testid="document-lines">
            <div className="drawer-section-title">Rincian Produk</div>
            <table className="doc-line-table v2">
              <thead><tr><th>PRODUK / DESKRIPSI</th><th className="right">QTY</th><th className="right">HARGA</th><th className="right">JUMLAH</th></tr></thead>
              <tbody>
                {selected.lines.map((l, i) => (
                  <tr key={i}><td>{l.product_name || l.description}</td><td className="right mono">{l.quantity ?? "—"}</td><td className="right mono">{l.price != null ? money(l.price) : "—"}</td><td className="right mono">{money(l.amount)}</td></tr>
                ))}
              </tbody>
            </table>
            <div className="doc-totals v2">
              {selected.subtotal > 0 && <div><span>Subtotal</span><b>{money(selected.subtotal)}</b></div>}
              {selected.tax_amount > 0 && <div><span>PPN {selected.tax_percent}%</span><b>{money(selected.tax_amount)}</b></div>}
              <div className="grand"><span>Total</span><b>{money(selected.total)}</b></div>
              <div><span>Sudah dibayar</span><b>{money(selected.paid || 0)}</b></div>
              <div className="outstanding" data-testid="document-outstanding"><span>Sisa tagihan</span><b>{money(outstanding)}</b></div>
            </div>
          </div>
        )}
        {isDoc && (
          <>
            <div className="drawer-section-title">Bagikan Dokumen</div>
            <div className="pdf-actions" data-testid="pdf-actions">
              <button data-testid="workflow-download-pdf" className="secondary-btn small" disabled={pdfBusy} onClick={downloadPdf}><Download size={14} /> {pdfBusy ? "Menyiapkan..." : "Unduh PDF"}</button>
              <button data-testid="workflow-share-whatsapp" className="secondary-btn small wa" onClick={shareWhatsApp}><MessageCircle size={14} /> WhatsApp</button>
              <button data-testid="workflow-share-email" className="secondary-btn small" onClick={shareEmail}><Mail size={14} /> Email</button>
            </div>
          </>
        )}
        {pdfError && <div className="success-message error" data-testid="pdf-error">{pdfError}</div>}
        {cfg.transaction && (
          <>
            <div className="drawer-section-title">Tindakan</div>
            <div className="drawer-actions v2">
              {selected.status === "DRAFT" && <button data-testid="workflow-approve-button" className="secondary-btn" onClick={() => onAction("approve")}>Setujui</button>}
              {["OPEN", "PARTIAL"].includes(selected.status) && outstanding > 0 && <button data-testid="workflow-pay-button" className="primary-btn" onClick={onPay}>{cfg.payLabel}</button>}
              {["DRAFT", "OPEN"].includes(selected.status) && !selected.journal_id && <button data-testid="workflow-post-button" className="secondary-btn" onClick={() => onAction("post")}>Posting ke jurnal</button>}
              {selected.status !== "VOID" && <button data-testid="workflow-edit-button" className="secondary-btn" onClick={onEdit}><Pencil size={14} /> Edit</button>}
              <button data-testid="workflow-delete-button" className="danger-btn" onClick={onDelete}><Trash2 size={14} /> Hapus</button>
            </div>
          </>
        )}
        {(cfg.endpoint === "/contacts" || cfg.endpoint === "/products") && (
          <>
            <div className="drawer-section-title">Tindakan</div>
            <div className="drawer-actions v2">
              <button data-testid="workflow-edit-button" className="secondary-btn" onClick={onEdit}><Pencil size={14} /> Edit</button>
              <button data-testid="workflow-delete-button" className="danger-btn" onClick={onDelete}><Trash2 size={14} /> Hapus</button>
            </div>
          </>
        )}
        {cfg.endpoint === "/journals" && (
          <div className="journal-lines">
            <div className="drawer-section-title">Baris Jurnal</div>
            {(selected.lines || []).map((l, i) => <div key={i}><span>{l.account_code} · {l.description}</span><b>{money(l.debit || l.credit)}</b></div>)}
          </div>
        )}
      </div>
    </div>
    </>
  );
}

function RecordForm({ cfg, initial, onCancel, onSave }) {
  const [form, setForm] = useState(initial || {});
  const set = (k, v) => setForm({ ...form, [k]: v });
  return (
    <div className="form-panel surface" data-testid="workflow-form">
      <div className="section-head">
        <div><p className="eyebrow">FORMULIR BARU</p><h2>{initial ? "Edit" : "Buat"} {cfg.singular}</h2></div>
        <button data-testid="workflow-cancel-button" className="icon-btn" onClick={onCancel}><X size={18} /></button>
      </div>
      <form className="workflow-record-form" onSubmit={(e) => { e.preventDefault(); onSave(form); }}>
        <div className="form-grid">
          {cfg.fields.map((field) => (
            <label key={field}>{field.replaceAll("_", " ")}
              <input data-testid={`workflow-field-${field}`} type={["date", "due_date"].includes(field) ? "date" : ["total", "selling_price", "stock"].includes(field) ? "number" : "text"} value={form[field] || ""} onChange={(e) => set(field, e.target.value)} required={["name", "customer", "supplier", "total", "description"].includes(field)} />
            </label>
          ))}
        </div>
        <div className="form-actions">
          <button data-testid="workflow-save-button" className="primary-btn" type="submit">Simpan sebagai draft</button>
          <button data-testid="workflow-form-cancel-button" className="secondary-btn" type="button" onClick={onCancel}>Batal</button>
        </div>
      </form>
    </div>
  );
}

const emptyLine = { account_code: "", description: "", debit: "", credit: "" };

function JournalForm({ onCancel, onSave }) {
  const [accounts, setAccounts] = useState([]);
  const [head, setHead] = useState({ date: new Date().toISOString().slice(0, 10), reference: "", description: "" });
  const [lines, setLines] = useState([{ ...emptyLine }, { ...emptyLine }]);
  useEffect(() => { api("/accounts").then(setAccounts).catch(() => {}); }, []);
  const setLine = (index, key, value) => setLines(lines.map((l, i) => (i === index ? { ...l, [key]: value } : l)));
  const totalDebit = lines.reduce((s, l) => s + (Number(l.debit) || 0), 0);
  const totalCredit = lines.reduce((s, l) => s + (Number(l.credit) || 0), 0);
  const incomplete = lines.some((l) => (Number(l.debit) || Number(l.credit)) && !l.account_code);
  const balanced = totalDebit > 0 && totalDebit === totalCredit && !incomplete;
  const submit = () => onSave({ ...head, lines: lines.filter((l) => l.account_code).map((l) => ({ account_code: l.account_code, description: l.description, debit: Number(l.debit) || 0, credit: Number(l.credit) || 0 })) });
  return (
    <div className="form-panel surface journal-form" data-testid="journal-form">
      <div className="section-head">
        <div><p className="eyebrow">FORMULIR JURNAL</p><h2>Buat Jurnal Umum</h2></div>
        <button data-testid="journal-cancel-button" className="icon-btn" onClick={onCancel}><X size={18} /></button>
      </div>
      <div className="form-grid">
        <label>tanggal<input data-testid="journal-field-date" type="date" value={head.date} onChange={(e) => setHead({ ...head, date: e.target.value })} required /></label>
        <label>referensi<input data-testid="journal-field-reference" value={head.reference} onChange={(e) => setHead({ ...head, reference: e.target.value })} /></label>
        <label>deskripsi<input data-testid="journal-field-description" value={head.description} onChange={(e) => setHead({ ...head, description: e.target.value })} required /></label>
      </div>
      <table className="journal-line-table" data-testid="journal-line-table">
        <thead><tr><th>AKUN</th><th>DESKRIPSI</th><th className="right">DEBIT</th><th className="right">KREDIT</th><th /></tr></thead>
        <tbody>
          {lines.map((line, i) => (
            <tr key={i}>
              <td>
                <select data-testid={`journal-line-account-${i}`} value={line.account_code} onChange={(e) => setLine(i, "account_code", e.target.value)}>
                  <option value="">Pilih akun...</option>
                  {accounts.map((a) => <option key={a.code} value={a.code}>{`${a.code} · ${a.name}`}</option>)}
                </select>
              </td>
              <td><input data-testid={`journal-line-description-${i}`} value={line.description} onChange={(e) => setLine(i, "description", e.target.value)} placeholder="Keterangan baris" /></td>
              <td><input data-testid={`journal-line-debit-${i}`} className="right" type="number" min="0" value={line.debit} onChange={(e) => setLine(i, "debit", e.target.value)} /></td>
              <td><input data-testid={`journal-line-credit-${i}`} className="right" type="number" min="0" value={line.credit} onChange={(e) => setLine(i, "credit", e.target.value)} /></td>
              <td>{lines.length > 2 && <button data-testid={`journal-line-remove-${i}`} className="icon-btn" onClick={() => setLines(lines.filter((_, x) => x !== i))}><Trash2 size={15} /></button>}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={2}><button data-testid="journal-add-line-button" className="text-btn" onClick={() => setLines([...lines, { ...emptyLine }])}><Plus size={14} /> Tambah baris</button></td>
            <td className="right mono" data-testid="journal-total-debit">{money(totalDebit)}</td>
            <td className="right mono" data-testid="journal-total-credit">{money(totalCredit)}</td>
            <td />
          </tr>
        </tfoot>
      </table>
      <div className={`journal-balance ${balanced ? "ok" : "bad"}`} data-testid="journal-balance-indicator">
        {incomplete ? "Setiap baris dengan nilai harus memiliki akun" : balanced ? "Jurnal seimbang dan siap diposting" : "Total debit dan kredit harus sama sebelum posting"}
      </div>
      <div className="form-actions">
        <button data-testid="journal-save-button" className="primary-btn" disabled={!balanced || !head.description} onClick={submit}>Posting jurnal</button>
        <button data-testid="journal-form-cancel-button" className="secondary-btn" onClick={onCancel}>Batal</button>
      </div>
    </div>
  );
}

const BULK_METHODS = ["Tunai", "Transfer Bank", "Cek", "Giro", "Kartu Kredit"];
const todayISO = () => new Date().toISOString().slice(0, 10);

function BulkPaymentModal({ cfg, docs, onClose, onDone, onError }) {
  useEscape(onClose);
  const isReceive = cfg.payMode === "RECEIVE";
  const partyLabel = isReceive ? "Pelanggan" : "Supplier";
  const [accounts, setAccounts] = useState([]);
  const [form, setForm] = useState({ account_code: "", date: todayISO(), method: "Transfer Bank", reference: "", memo: "" });
  const [amounts, setAmounts] = useState(() => Object.fromEntries(docs.map((d) => [d.id, String(Math.max(0, (d.total || 0) - (d.paid || 0)))])));
  const [saving, setSaving] = useState(false);
  useEffect(() => { api("/cash-accounts").then(setAccounts).catch(() => {}); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const rows = docs.map((d) => ({ ...d, outstanding: (d.total || 0) - (d.paid || 0), party: d.customer || d.supplier || "" }));
  const total = rows.reduce((s, d) => s + (Number(amounts[d.id]) || 0), 0);
  const overpaid = rows.some((d) => (Number(amounts[d.id]) || 0) > d.outstanding + 0.01);
  const anyPositive = rows.some((d) => (Number(amounts[d.id]) || 0) > 0);
  const valid = form.account_code && form.date && anyPositive && !overpaid && !saving;
  const payFull = (d) => setAmounts((a) => ({ ...a, [d.id]: String(d.outstanding) }));
  const submit = async () => {
    setSaving(true);
    try {
      const allocations = rows.filter((d) => (Number(amounts[d.id]) || 0) > 0).map((d) => ({ document_id: d.id, amount: Number(amounts[d.id]) }));
      const res = await api("/payments/bulk", { method: "POST", body: JSON.stringify({ type: cfg.payMode, account_code: form.account_code, date: form.date, method: form.method, reference: form.reference, memo: form.memo, allocations }) });
      onDone(res);
    } catch (e) { onError(e.message); setSaving(false); }
  };
  return (
    <div className="modal-overlay" data-testid="bulk-payment-modal" onClick={onClose}>
      <div className="modal-panel wide" onClick={(e) => e.stopPropagation()}>
        <div className="form-panel">
          <div className="section-head">
            <div><p className="eyebrow">{isReceive ? "TERIMA SEKALIGUS" : "BAYAR SEKALIGUS"}</p><h2>{isReceive ? "Terima Pembayaran Sekaligus" : "Bayar Beberapa Tagihan Sekaligus"}</h2></div>
            <button data-testid="bulk-pay-close" className="icon-btn" onClick={onClose}><X size={18} /></button>
          </div>
          <p className="muted">{rows.length} dokumen terpilih. Pembayaran dikelompokkan otomatis per {partyLabel.toLowerCase()}. Ubah nilai pada kolom jumlah untuk pembayaran sebagian.</p>
          <div className="form-grid payment-head-grid">
            <label>{isReceive ? "Setor ke akun" : "Bayar dari akun"} *
              <SearchSelect testid="bulk-pay-account" value={form.account_code} onChange={(v) => set("account_code", v)} placeholder="Pilih akun kas/bank..."
                options={accounts.map((a) => ({ value: a.code, label: `${a.code} · ${a.name}`, sub: money(a.balance) }))} />
            </label>
            <label>Tanggal *<input data-testid="bulk-pay-date" type="date" value={form.date} onChange={(e) => set("date", e.target.value)} /></label>
            <label>Metode pembayaran
              <select data-testid="bulk-pay-method" value={form.method} onChange={(e) => set("method", e.target.value)}>
                {BULK_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </label>
            <label>No. referensi<input data-testid="bulk-pay-reference" value={form.reference} onChange={(e) => set("reference", e.target.value)} placeholder="Opsional" /></label>
          </div>
          <table className="journal-line-table" data-testid="bulk-pay-table">
            <thead><tr><th>NOMOR</th><th>{partyLabel.toUpperCase()}</th><th className="right">SISA TAGIHAN</th><th className="right">JUMLAH BAYAR</th><th /></tr></thead>
            <tbody>
              {rows.map((d) => (
                <tr key={d.id} data-testid={`bulk-pay-row-${d.id}`}>
                  <td className="mono">{d.number}</td>
                  <td>{d.party}</td>
                  <td className="right mono">{money(d.outstanding)}</td>
                  <td><input data-testid={`bulk-pay-amount-${d.id}`} className={`right ${(Number(amounts[d.id]) || 0) > d.outstanding + 0.01 ? "over" : ""}`} type="number" min="0" max={d.outstanding} value={amounts[d.id]} onChange={(e) => setAmounts((a) => ({ ...a, [d.id]: e.target.value }))} /></td>
                  <td><button type="button" className="text-btn" data-testid={`bulk-pay-full-${d.id}`} onClick={() => payFull(d)}>Penuh</button></td>
                </tr>
              ))}
            </tbody>
          </table>
          {overpaid && <div className="journal-balance bad" data-testid="bulk-pay-overpaid">Ada jumlah yang melebihi sisa tagihan</div>}
          <div className="bulk-pay-total" data-testid="bulk-pay-total"><span>Total {isReceive ? "penerimaan" : "pembayaran"}</span><strong>{money(total)}</strong></div>
          <div className="form-actions">
            <button data-testid="bulk-pay-submit" className="primary-btn" disabled={!valid} onClick={submit}>{saving ? "Memproses..." : `${isReceive ? "Terima" : "Bayar"} ${money(total)}`}</button>
            <button data-testid="bulk-pay-cancel" className="secondary-btn" onClick={onClose}>Batal</button>
          </div>
        </div>
      </div>
    </div>
  );
}
